import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, signal, input, output, computed, linkedSignal, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, RouterOutlet } from '@angular/router';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { entityOutletBridgeStyles, entityOutletStyleId } from './entity.outlet.component.theme';

export enum EntityOutletMode {
    Dialog = 'dialog',
    Inline = 'inline'
}

@Component({
    selector: 'entity-outlet',
    template: `
        @if ($backdrop()) {
            <div class="entity-outlet-backdrop"></div>
        }

        <ng-template #outlet>
            @if($name()) {
                <router-outlet #outlet [name]="$name()" [routerOutletData]="$data()" (activate)="onActivate($event)" (deactivate)="onDeactivate()"/>
            } @else {
                <router-outlet #outlet [routerOutletData]="$data()" (activate)="onActivate($event)" (deactivate)="onDeactivate()"/>
            }
        </ng-template>

        @if ($isDialog()) {
            <div class="entity-outlet-content">
                <ng-container *ngTemplateOutlet="outlet" />
            </div>
        } @else {
            <ng-container *ngTemplateOutlet="outlet" />
        }
        
    `,
    styleUrls: ['entity.outlet.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule
    ],
    host: {
        '[class.entity-outlet-open]': '$visible()',
        '[class.entity-outlet-dialog]': '$visible() && $effectiveMode() === EntityOutletMode.Dialog',
        '[class.entity-outlet-inline]': '$visible() && $effectiveMode() === EntityOutletMode.Inline',
    },
})
export class EntityOutletComponent {

    private readonly document = inject(DOCUMENT);
    protected EntityOutletMode = EntityOutletMode;
    protected route = inject(ActivatedRoute);

    public constructor() {

        DesignSystem.Mount(this.document, entityOutletStyleId, entityOutletBridgeStyles);
    }

    public $name = input(null, { alias: 'name' });
    public $data = input(null, { alias: 'data' });
    public $mode = input(EntityOutletMode.Inline, { alias: 'mode' });

    public activate = output<void>();
    public deactivate = output<void>();

    protected $outlet = viewChild(RouterOutlet, { read: RouterOutlet });
    protected $effectiveMode = linkedSignal(this.$mode);

    protected $visible = signal(false);
    protected $isDialog = computed(() => this.$effectiveMode() === EntityOutletMode.Dialog);
    protected $isInline = computed(() => this.$effectiveMode() === EntityOutletMode.Inline);
    protected $backdrop = computed(() => this.$isDialog() && this.$visible());

    protected onActivate(data: any) {

        const route = this.$outlet()?.activatedRoute;
        const mode = route?.snapshot?.data?.entityOutletMode ?? this.$mode();
        this.$effectiveMode.set(mode);

        this.activate.emit();
        this.$visible.set(true);
    }

    protected onDeactivate() {
        this.deactivate.emit();
        this.$visible.set(false);
    }

}